import { useState } from 'react';
import { Mail, Phone, MessageCircle, MapPin } from 'lucide-react';
import { contactInfo } from '@/mocks/homeData';

const iconMap: Record<string, React.ReactNode> = {
  Mail: <Mail className="w-5 h-5 text-gold-500" />,
  Phone: <Phone className="w-5 h-5 text-gold-500" />,
  MessageCircle: <MessageCircle className="w-5 h-5 text-gold-500" />,
  MapPin: <MapPin className="w-5 h-5 text-gold-500" />,
};

export default function ContactSection() {
  const [formState, setFormState] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    userType: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('sending');

    const form = e.target as HTMLFormElement;
    const formDataObj = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d7nj4sf5qk5pqai6cr20', {
        method: 'POST',
        body: formDataObj,
      });

      if (response.ok) {
        setFormState('success');
        setFormData({ fullName: '', email: '', phone: '', userType: '', message: '' });
        setTimeout(() => setFormState('idle'), 4000);
      } else {
        setFormState('error');
        setTimeout(() => setFormState('idle'), 4000);
      }
    } catch {
      setFormState('error');
      setTimeout(() => setFormState('idle'), 4000);
    }
  };

  return (
    <section id="contact" className="bg-gray-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="text-xs font-semibold tracking-widest text-gold-600 uppercase">Contact Us</span>
            <h2 className="mt-3 text-3xl md:text-4xl lg:text-5xl font-bold text-navy-600">
              We&apos;d Love to Hear From You
            </h2>
            <p className="mt-4 text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Have a question about EzyAbroad? Want to see a demo? Reach out — our team responds within 24 hours.
            </p>
          </div>

          <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
            {/* Left: Contact Info */}
            <div className="lg:col-span-2 space-y-6">
              {contactInfo.map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center flex-shrink-0">
                    {iconMap[item.icon]}
                  </div>
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">{item.label}</div>
                    <div className="text-sm font-medium text-navy-600 mt-0.5">{item.value}</div>
                  </div>
                </div>
              ))}

              {/* Map placeholder */}
              <div className="mt-6 rounded-xl overflow-hidden h-48 bg-gray-200">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d243647.31698249052!2d78.267959!3d17.412299!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb99daeaebd2c7%3A0x3ae5e2c8cd6d6d1a!2sHyderabad%2C%20Telangana%2C%20India!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="EzyAbroad Office Location"
                />
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="lg:col-span-3">
              <form
                id="ezyabroad-contact"
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white rounded-xl p-6 md:p-8 shadow-sm"
              >
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-navy-600 mb-1.5">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      placeholder="Enter your full name"
                      required
                      minLength={2}
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-600 mb-1.5">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="Enter your email"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-navy-600 mb-1.5">Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Enter your phone number"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-navy-600 mb-1.5">I am a...</label>
                    <select
                      name="userType"
                      value={formData.userType}
                      onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors bg-white"
                    >
                      <option value="">Select one</option>
                      <option value="Student">Student</option>
                      <option value="Consultancy">Consultancy</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-navy-600 mb-1.5">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="Tell us how we can help you..."
                    required
                    minLength={20}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors resize-none"
                  />
                  <div className="text-xs text-gray-400 mt-1 text-right">{formData.message.length}/500</div>
                </div>

                <button
                  type="submit"
                  disabled={formState === 'sending'}
                  className="mt-4 w-full py-3.5 bg-gold-500 text-navy-700 font-semibold rounded-lg hover:bg-gold-400 transition-all flex items-center justify-center gap-2 disabled:opacity-70 cursor-pointer"
                >
                  {formState === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>

                {/* Success/Error Toast */}
                {formState === 'success' && (
                  <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
                    <i className="ri-check-line text-green-500" />
                    <span className="text-sm text-green-700">Your message has been sent! We will get back to you within 24 hours.</span>
                  </div>
                )}
                {formState === 'error' && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500" />
                    <span className="text-sm text-red-700">Oops! Something went wrong. Please try again or email us directly.</span>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
